import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Loader2, Users, CheckCircle, XCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function TeamInvitePage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Team Invite", noIndex: true });
  const [status, setStatus] = useState("loading");
  const [teamName, setTeamName] = useState("");
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!user) {
      setStatus("need_login");
    } else {
      setStatus("ready");
    }
  }, [user]);

  const joinTeam = async () => {
    setJoining(true); setError("");
    try {
      const { data } = await api.post(`/team/join/${token}`);
      setTeamName(data.team_name || "");
      setStatus("joined");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to join team");
      setStatus("error");
    }
    finally { setJoining(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="team-invite-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 p-8 max-w-md w-full mx-4 text-center">
        {status === "loading" && <Loader2 className="w-8 h-8 animate-spin text-[#0033CC] mx-auto" />}

        {status === "need_login" && (
          <>
            <Users className="w-10 h-10 text-[#0033CC] mx-auto mb-4" />
            <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Team Invite</h1>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">Sign in to accept this team invite.</p>
            <Button onClick={() => navigate(`/auth?redirect=/team-invite/${token}`)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 px-8 font-['Outfit']" data-testid="login-to-join">
              Sign In to Join
            </Button>
          </>
        )}

        {status === "ready" && (
          <>
            <Users className="w-10 h-10 text-[#0033CC] mx-auto mb-4" />
            <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Join Team</h1>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">You've been invited to join a team on Stansbury AI. As a member, you'll have access to shared blueprints and team insights.</p>
            <Button onClick={joinTeam} disabled={joining} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 px-8 font-['Outfit']" data-testid="accept-invite-btn">
              {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
              Accept Invite
            </Button>
          </>
        )}

        {status === "joined" && (
          <>
            <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Welcome to {teamName || "the team"}!</h1>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">You can now view shared blueprints on your dashboard.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 px-8 font-['Outfit']" data-testid="go-to-dashboard">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Invite Error</h1>
            <p className="text-sm text-red-600 font-['IBM_Plex_Sans'] mb-6">{error}</p>
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-none h-11 px-8 font-['Outfit']" data-testid="back-to-dashboard">
              Back to Dashboard
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
